/**
 * DecisionFlow — TenantDeprovision
 * ui/src/components/platform-admin/TenantDeprovision.jsx
 * OI-S225-01 · m_s490_01 · S626: bulk delete support
 *
 * Confirmation modal for DELETE /api/v1/admin/tenants/{id}/deprovision.
 * Supports single and bulk mode — caller passes tenants[] (always an array).
 *
 * Bulk mode: serial calls, one per tenant. Progress shown as "Deleting 2 of 5…"
 * Partial success: records which tenants deleted OK and which failed.
 * onSuccess called with array of successfully deleted tenant IDs.
 *
 * Backend guards (server-enforced per call):
 *   - tenant must exist and be is_demo=TRUE
 *   - approver email+password verified against user_account (argon2id)
 *   - approver must have active platform-level platform_admin role
 *   - actor (tool_admin session) ≠ approver (two-person rule)
 *   - fn_reset_tenant_data enforces all guards again at DB level
 *
 * Auth: session cookie (credentials: "include") — same as all other routes.
 * DELETE body: { approver_email, approver_password }
 *
 * Props:
 *   tenants   — tenant object[] (always array, even for single)
 *               each: { tenant_id, tenant_name, tenant_slug }
 *   onSuccess — (deletedIds: string[]) => void
 *   onCancel  — () => void
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, X, Eye, EyeOff, CheckCircle, XCircle } from "lucide-react";

import Button from "../shared/Button.jsx";

const API_BASE = "/api/v1";

export default function TenantDeprovision({ tenants, onSuccess, onCancel }) {
  const { t } = useTranslation("platform");

  const isBulk = tenants.length > 1;

  const [approverEmail,    setApproverEmail]    = useState("");
  const [approverPassword, setApproverPassword] = useState("");
  const [showPassword,     setShowPassword]     = useState(false);
  const [submitting,       setSubmitting]       = useState(false);
  const [progress,         setProgress]         = useState(null); // { current, total, label }
  const [results,          setResults]          = useState(null); // null | { succeeded: [], failed: [] }
  const [error,            setError]            = useState(null);

  async function deleteSingle(tenant, email, password) {
    const res = await fetch(
      `${API_BASE}/admin/tenants/${tenant.tenant_id}/deprovision`,
      {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          approver_email:    email,
          approver_password: password,
        }),
      }
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body?.detail?.error?.message ?? `Error ${res.status}`);
    }
  }

  async function handleConfirm() {
    if (!approverEmail.trim() || !approverPassword) {
      setError("Approver email and password are required.");
      return;
    }

    setSubmitting(true);
    setError(null);
    setResults(null);

    const succeeded = [];
    const failed    = [];

    for (let i = 0; i < tenants.length; i++) {
      const tenant = tenants[i];
      setProgress({ current: i + 1, total: tenants.length, label: tenant.tenant_name });
      try {
        await deleteSingle(tenant, approverEmail.trim(), approverPassword);
        succeeded.push(tenant.tenant_id);
      } catch (e) {
        failed.push({ tenant, message: e.message });
      }
    }

    setSubmitting(false);
    setProgress(null);

    if (failed.length === 0) {
      // All succeeded — close immediately
      onSuccess(succeeded);
    } else {
      // Partial or total failure — show results summary
      setResults({ succeeded, failed });
    }
  }

  function handleDone() {
    if (results?.succeeded.length > 0) {
      onSuccess(results.succeeded);
    } else {
      onCancel();
    }
  }

  const canSubmit = approverEmail.trim() && approverPassword && !submitting;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
          <div className="flex items-center gap-2.5">
            <AlertTriangle className="h-5 w-5 shrink-0 text-[rgb(var(--color-danger))]" />
            <h2 className="text-tok-base font-bold text-[rgb(var(--color-text))]">
              {isBulk
                ? `Delete ${tenants.length} Demo Tenants`
                : t("deprovision.title")}
            </h2>
          </div>
          <button
            onClick={onCancel}
            disabled={submitting}
            className="rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] transition disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="px-5 py-4 space-y-4 overflow-y-auto">

          {/* Results state (partial/total failure) */}
          {results ? (
            <div className="space-y-3">
              {results.succeeded.length > 0 && (
                <div className="flex items-center gap-2 rounded-xl bg-[rgb(var(--color-success))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-success))]">
                  <CheckCircle className="h-4 w-4 shrink-0" />
                  {results.succeeded.length} tenant{results.succeeded.length !== 1 ? "s" : ""} deleted successfully.
                </div>
              )}
              {results.failed.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-tok-sm font-medium text-[rgb(var(--color-danger))]">
                    <XCircle className="h-4 w-4 shrink-0" />
                    {results.failed.length} tenant{results.failed.length !== 1 ? "s" : ""} failed:
                  </div>
                  <div className="space-y-1.5 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2">
                    {results.failed.map(({ tenant, message }) => (
                      <div key={tenant.tenant_id} className="text-tok-xs">
                        <span className="font-medium text-[rgb(var(--color-text))]">{tenant.tenant_name}</span>
                        <span className="ml-2 text-[rgb(var(--color-danger))]">{message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <p className="text-tok-sm text-[rgb(var(--color-text))]">
                {isBulk
                  ? `You are about to permanently delete ${tenants.length} demo tenants. This action cannot be undone.`
                  : t("deprovision.confirm", { name: tenants[0].tenant_name })}
              </p>

              {/* Tenant list */}
              <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 space-y-2 max-h-48 overflow-y-auto">
                {tenants.map((tenant) => (
                  <div key={tenant.tenant_id} className="space-y-0.5">
                    <div className="flex items-center justify-between text-tok-xs">
                      <span className="text-[rgb(var(--color-text-muted))]">Name</span>
                      <span className="font-semibold text-[rgb(var(--color-text))]">{tenant.tenant_name}</span>
                    </div>
                    <div className="flex items-center justify-between text-tok-xs">
                      <span className="text-[rgb(var(--color-text-muted))]">Slug</span>
                      <span className="font-mono text-[rgb(var(--color-text))]">{tenant.tenant_slug}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4 text-tok-xs">
                      <span className="shrink-0 text-[rgb(var(--color-text-muted))]">ID</span>
                      <span className="break-all text-right font-mono text-tok-xs text-[rgb(var(--color-text-muted))]">{tenant.tenant_id}</span>
                    </div>
                    {isBulk && <div className="border-b border-[rgb(var(--color-border))] last:hidden mt-1.5" />}
                  </div>
                ))}
              </div>

              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                {t("deprovision.irreversible")}
              </p>

              {/* Approver credentials */}
              <div className="space-y-3 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
                <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
                  Platform admin approval required
                </p>

                <div className="space-y-1">
                  <label className="block text-tok-xs font-medium text-[rgb(var(--color-text))]">
                    Approver email
                  </label>
                  <input
                    type="email"
                    value={approverEmail}
                    onChange={(e) => setApproverEmail(e.target.value)}
                    placeholder="platform.admin@data-fit.com"
                    disabled={submitting}
                    autoComplete="off"
                    className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-tok-xs font-medium text-[rgb(var(--color-text))]">
                    Approver password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={approverPassword}
                      onChange={(e) => setApproverPassword(e.target.value)}
                      placeholder="••••••••"
                      disabled={submitting}
                      autoComplete="new-password"
                      className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 pr-10 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      disabled={submitting}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] transition disabled:opacity-50"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Progress indicator (bulk in flight) */}
              {submitting && progress && (
                <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text-muted))]">
                  Deleting {progress.current} of {progress.total}: <span className="font-medium text-[rgb(var(--color-text))]">{progress.label}</span>…
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
          {results ? (
            <Button variant="secondary" onClick={handleDone}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={onCancel} disabled={submitting}>
                {t("deprovision.cancel")}
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirm}
                disabled={!canSubmit}
              >
                {submitting
                  ? (isBulk ? `Deleting ${progress?.current ?? "…"} of ${tenants.length}…` : t("deprovision.deleting"))
                  : (isBulk ? `Delete ${tenants.length} tenants` : t("deprovision.deleteConfirm"))
                }
              </Button>
            </>
          )}
        </div>

      </div>
    </div>
  );
}
